import React, { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { createCatalyxRole, createClientRole } from './api';
import { roleKeyFromName } from './types';
import { fieldClass, helperClass, labelClass, primaryBtnClass } from '../styles';

interface CreateRoleProps {
  kind: 'catalyx' | 'client';
}

const CreateRole: React.FC<CreateRoleProps> = ({ kind }) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const keyPreview = useMemo(() => roleKeyFromName(name), [name]);
  const label = kind === 'catalyx' ? 'Catalyx role' : 'Client role';

  const submit = async () => {
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const created =
        kind === 'catalyx' ? await createCatalyxRole(name) : await createClientRole(name);
      const createdName = (created as { name?: string })?.name || name.trim();
      setSuccess(`Created ${createdName}. The new column is available on the capabilities grid.`);
      setName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl">
      <p className="text-sm text-[#626262] mb-5">
        Create a {label.toLowerCase()}. The key is generated from the name.
      </p>
      {error && (
        <div className="mb-4 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}
      {success && (
        <div className="mb-4 rounded-md bg-emerald-50 text-emerald-800 px-3 py-2 text-sm">
          {success}
        </div>
      )}
      <label className={labelClass}>
        Name <span className="text-red-600">*</span>
      </label>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        className={fieldClass}
        placeholder={kind === 'catalyx' ? 'Account Manager' : 'Creator'}
      />
      <p className={helperClass}>
        Key will be <code>{keyPreview || '—'}</code>
      </p>
      <div className="mt-5">
        <button type="button" disabled={saving} onClick={() => void submit()} className={primaryBtnClass}>
          {saving && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
          Create {label.toLowerCase()}
        </button>
      </div>
    </div>
  );
};

export default CreateRole;
